import React, { useState } from 'react';
import { Card, Button, Spinner, Alert, Row, Col, Badge, Table, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CarteV2Service from '../../../services/CarteV2Service';

/**
 * Composant pour la gestion des duplicatas avec paiement des frais
 */
const DuplicateCardManager = () => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [searching, setSearching] = useState(false);
    const [originalCard, setOriginalCard] = useState(null);
    const [beneficiary, setBeneficiary] = useState(null);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateReason, setDuplicateReason] = useState('');
    const [paymentData, setPaymentData] = useState({
        amount: 5000, // Frais standard de duplicata en FCFA
        paymentMethod: 'OrangeMoney',
        paymentReference: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paidBy: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);
    const [duplicateHistory, setDuplicateHistory] = useState([]);

    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            setMessage({ type: 'danger', text: t('duplicateCardManager.errors.cardNumberRequired') });
            return;
        }

        try {
            setSearching(true);
            setMessage(null);
            setOriginalCard(null);
            setBeneficiary(null);
            setDuplicateHistory([]);

            // Rechercher la carte par son numéro
            const searchResult = await CarteV2Service.searchCards({
                searchTerm: searchTerm,
                pageSize: 1
            });

            if (!searchResult.data || searchResult.data.length === 0) {
                setMessage({ type: 'warning', text: t('duplicateCardManager.messages.noCardFound') });
                return;
            }

            const card = searchResult.data[0];
            setOriginalCard(card);

            // Récupérer les détails du bénéficiaire
            if (card.beneficiaryId) {
                const beneficiaryDetails = await CarteV2Service.getBeneficiaryDetails(card.beneficiaryId);
                setBeneficiary(beneficiaryDetails);
                setPaymentData(prev => ({
                    ...prev,
                    paidBy: `${beneficiaryDetails.firstName} ${beneficiaryDetails.lastName}`
                }));
            }

            // Récupérer l'historique des duplicatas
            const history = await CarteV2Service.getDuplicateHistory(card.id);
            setDuplicateHistory(history.duplicates || []);

        } catch (err) {
            setMessage({ type: 'danger', text: t('duplicateCardManager.errors.searchFailed') + ': ' + err.message });
            console.error(err);
        } finally {
            setSearching(false);
        }
    };

    const handleOpenDuplicateModal = () => {
        setDuplicateReason('');
        setShowDuplicateModal(true);
        setMessage(null);
    };

    const handleCreateDuplicate = async () => {
        // Validation
        if (!duplicateReason.trim()) {
            setMessage({ type: 'danger', text: t('duplicateCardManager.errors.reasonRequired') });
            return;
        }
        if (!paymentData.paymentReference.trim()) {
            setMessage({ type: 'danger', text: t('duplicateCardManager.errors.referenceRequired') });
            return;
        }

        try {
            setSubmitting(true);
            setMessage(null);

            // Appel API réel pour créer le duplicata
            const result = await CarteV2Service.createDuplicate(originalCard.id, {
                reason: duplicateReason,
                payment: paymentData,
                requestedBy: 'admin@system.com'
            });

            if (result.success) {
                setMessage({
                    type: 'success',
                    text: t('duplicateCardManager.messages.duplicateCreated', {
                        amount: paymentData.amount.toLocaleString('fr-FR')
                    }) + (result.message ? ' ' + result.message : '')
                });

                // Fermer le modal et rafraîchir
                setTimeout(() => {
                    setShowDuplicateModal(false);
                    handleSearch(); // Rafraîchir les données
                }, 2000);
            } else {
                setMessage({
                    type: 'warning',
                    text: result.message || t('duplicateCardManager.messages.duplicateCreationFailed')
                });
            }

        } catch (err) {
            setMessage({
                type: 'danger',
                text: t('duplicateCardManager.errors.creationFailed') + ': ' + err.message
            });
        } finally {
            setSubmitting(false);
        }
    };

    const getDuplicateReasons = () => [
        { value: 'loss', label: t('duplicateCardManager.reasons.loss') },
        { value: 'theft', label: t('duplicateCardManager.reasons.theft') },
        { value: 'damage', label: t('duplicateCardManager.reasons.damage') },
        { value: 'unreadable', label: t('duplicateCardManager.reasons.unreadable') },
        { value: 'other', label: t('duplicateCardManager.reasons.other') }
    ];

    const getPaymentMethods = () => [
        { value: 'OrangeMoney', label: t('duplicateCardManager.paymentMethods.OrangeMoney') },
        { value: 'MTNMoney', label: t('duplicateCardManager.paymentMethods.MTNMoney') },
        { value: 'Moov', label: t('duplicateCardManager.paymentMethods.Moov') },
        { value: 'Wave', label: t('duplicateCardManager.paymentMethods.Wave') },
        { value: 'Cash', label: t('duplicateCardManager.paymentMethods.Cash') },
        { value: 'BankTransfer', label: t('duplicateCardManager.paymentMethods.BankTransfer') }
    ];

    return (
        <div className="duplicate-card-manager">
            <h4 className="mb-4">
                <i className="fa fa-copy me-2"></i>
                {t('duplicateCardManager.title')}
            </h4>

            {/* Zone de recherche */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">
                        <i className="fa fa-search me-2"></i>
                        {t('duplicateCardManager.searchOriginalCard')}
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={8}>
                            <div className="mb-3">
                                <label className="form-label">{t('duplicateCardManager.cardNumber')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    placeholder={t('duplicateCardManager.placeholders.cardNumber')}
                                />
                            </div>
                        </Col>
                        <Col md={4} className="d-flex align-items-end">
                            <Button
                                variant="primary"
                                className="w-100 mb-3"
                                onClick={handleSearch}
                                disabled={searching}
                            >
                                {searching ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        {t('duplicateCardManager.searching')}
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-search me-2"></i>
                                        {t('duplicateCardManager.search')}
                                    </>
                                )}
                            </Button>
                        </Col>
                    </Row>

                    {message && (
                        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
                            {message.text}
                        </Alert>
                    )}
                </Card.Body>
            </Card>

            {/* Informations de la carte et du bénéficiaire */}
            {originalCard && beneficiary && (
                <>
                    <Row>
                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Header className="bg-primary text-white">
                                    <h6 className="mb-0">
                                        <i className="fa fa-id-card me-2"></i>
                                        {t('duplicateCardManager.originalCard')}
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="mb-2">
                                        <strong>{t('duplicateCardManager.cardNumber')}:</strong> <code>{originalCard.cardNumber}</code>
                                    </div>
                                    <div className="mb-2">
                                        <strong>{t('duplicateCardManager.labels.cardType')}:</strong> <Badge bg="primary">{originalCard.cardType}</Badge>
                                    </div>
                                    <div className="mb-2">
                                        <strong>{t('duplicateCardManager.labels.status')}:</strong> <Badge bg="success">{originalCard.status}</Badge>
                                    </div>
                                    <div className="mb-2">
                                        <strong>{t('duplicateCardManager.labels.productionDate')}:</strong> {new Date(originalCard.productionDate).toLocaleDateString('fr-FR')}
                                    </div>
                                    <div className="mb-2">
                                        <strong>{t('duplicateCardManager.labels.expiryDate')}:</strong> {new Date(originalCard.expiryDate).toLocaleDateString('fr-FR')}
                                    </div>
                                    <div className="mb-2">
                                        <strong>{t('duplicateCardManager.labels.duplicateCount')}:</strong> <Badge bg="info">{originalCard.duplicateCount}</Badge>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Header className="bg-info text-white">
                                    <h6 className="mb-0">
                                        <i className="fa fa-user me-2"></i>
                                        {t('duplicateCardManager.beneficiary')}
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="mb-2">
                                        <strong>{t('duplicateCardManager.labels.fullName')}:</strong> {beneficiary.firstName} {beneficiary.lastName}
                                    </div>
                                    <div className="mb-2">
                                        <strong>{t('duplicateCardManager.labels.beneficiaryNumber')}:</strong> <code>{beneficiary.beneficiaryNumber}</code>
                                    </div>
                                    <div className="mb-2">
                                        <strong>{t('duplicateCardManager.labels.policyNumber')}:</strong> <code>{beneficiary.policyNumber}</code>
                                    </div>
                                    <div className="mb-2">
                                        <strong>{t('duplicateCardManager.labels.phone')}:</strong> {beneficiary.phone}
                                    </div>
                                    <div className="mb-2">
                                        <strong>{t('duplicateCardManager.labels.dateOfBirth')}:</strong> {new Date(beneficiary.dateOfBirth).toLocaleDateString('fr-FR')}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Bouton de création de duplicata */}
                    <Card className="mb-4 border-warning">
                        <Card.Body className="text-center">
                            <h5 className="mb-3">
                                <i className="fa fa-copy me-2"></i>
                                {t('duplicateCardManager.createDuplicate')}
                            </h5>
                            <p className="text-muted mb-3">
                                {t('duplicateCardManager.duplicateFees')}: <strong>{paymentData.amount.toLocaleString('fr-FR')} FCFA</strong>
                            </p>
                            <Button
                                variant="warning"
                                size="lg"
                                onClick={handleOpenDuplicateModal}
                            >
                                <i className="fa fa-plus-circle me-2"></i>
                                {t('duplicateCardManager.createDuplicate')}
                            </Button>
                        </Card.Body>
                    </Card>

                    {/* Historique des duplicatas */}
                    {duplicateHistory.length > 0 && (
                        <Card>
                            <Card.Header>
                                <h5 className="mb-0">
                                    <i className="fa fa-history me-2"></i>
                                    {t('duplicateCardManager.duplicateHistory')} ({duplicateHistory.length})
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="table-responsive">
                                    <Table hover>
                                        <thead>
                                            <tr>
                                                <th>{t('duplicateCardManager.labels.duplicateNumber')}</th>
                                                <th>{t('duplicateCardManager.labels.reason')}</th>
                                                <th>{t('duplicateCardManager.labels.requestDate')}</th>
                                                <th>{t('duplicateCardManager.labels.deliveryDate')}</th>
                                                <th>{t('duplicateCardManager.labels.paymentAmount')}</th>
                                                <th>{t('duplicateCardManager.paymentMethod')}</th>
                                                <th>{t('duplicateCardManager.paymentReference')}</th>
                                                <th>{t('duplicateCardManager.labels.status')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {duplicateHistory.map(dup => (
                                                <tr key={dup.id}>
                                                    <td><code>{dup.duplicateCardNumber}</code></td>
                                                    <td>{dup.reason}</td>
                                                    <td>{new Date(dup.requestDate).toLocaleDateString('fr-FR')}</td>
                                                    <td>{dup.completionDate ? new Date(dup.completionDate).toLocaleDateString('fr-FR') : '-'}</td>
                                                    <td><strong>{dup.paymentAmount.toLocaleString('fr-FR')} FCFA</strong></td>
                                                    <td><Badge bg="info">{dup.paymentMethod}</Badge></td>
                                                    <td><code>{dup.paymentReference}</code></td>
                                                    <td><Badge bg="success">{dup.status}</Badge></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </>
            )}

            {/* Modal de création de duplicata */}
            <Modal show={showDuplicateModal} onHide={() => setShowDuplicateModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa fa-copy me-2"></i>
                        {t('duplicateCardManager.createDuplicateModal')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {message && (
                        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
                            {message.text}
                        </Alert>
                    )}

                    {/* Informations récapitulatives */}
                    <Alert variant="info">
                        <Row>
                            <Col md={6}>
                                <strong>{t('duplicateCardManager.originalCard')}:</strong> <code>{originalCard?.cardNumber}</code>
                            </Col>
                            <Col md={6}>
                                <strong>{t('duplicateCardManager.beneficiary')}:</strong> {beneficiary?.firstName} {beneficiary?.lastName}
                            </Col>
                        </Row>
                    </Alert>

                    {/* Raison du duplicata */}
                    <div className="mb-3">
                        <label className="form-label">{t('duplicateCardManager.duplicateReason')} *</label>
                        <select
                            className="form-select"
                            value={duplicateReason}
                            onChange={(e) => setDuplicateReason(e.target.value)}
                        >
                            <option value="">{t('duplicateCardManager.reasons.selectReason')}</option>
                            {getDuplicateReasons().map(reason => (
                                <option key={reason.value} value={reason.value}>{reason.label}</option>
                            ))}
                        </select>
                    </div>

                    <hr />

                    <h6 className="mb-3">
                        <i className="fa fa-credit-card me-2"></i>
                        {t('duplicateCardManager.paymentInfo')}
                    </h6>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <label className="form-label">{t('duplicateCardManager.amount')} *</label>
                                <div className="input-group">
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={paymentData.amount}
                                        onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) })}
                                    />
                                    <span className="input-group-text">FCFA</span>
                                </div>
                                <small className="text-muted">{t('duplicateCardManager.labels.standardAmount')}</small>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <label className="form-label">{t('duplicateCardManager.paymentMethod')} *</label>
                                <select
                                    className="form-select"
                                    value={paymentData.paymentMethod}
                                    onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                                >
                                    {getPaymentMethods().map(method => (
                                        <option key={method.value} value={method.value}>{method.label}</option>
                                    ))}
                                </select>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <label className="form-label">{t('duplicateCardManager.paymentReference')} *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={paymentData.paymentReference}
                                    onChange={(e) => setPaymentData({ ...paymentData, paymentReference: e.target.value })}
                                    placeholder={t('duplicateCardManager.placeholders.paymentReference')}
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <label className="form-label">{t('duplicateCardManager.paymentDate')}</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={paymentData.paymentDate}
                                    onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                                />
                            </div>
                        </Col>
                    </Row>

                    <div className="mb-3">
                        <label className="form-label">{t('duplicateCardManager.paidBy')}</label>
                        <input
                            type="text"
                            className="form-control"
                            value={paymentData.paidBy}
                            onChange={(e) => setPaymentData({ ...paymentData, paidBy: e.target.value })}
                            placeholder={t('duplicateCardManager.placeholders.paidBy')}
                        />
                    </div>

                    <Alert variant="warning">
                        <i className="fa fa-info-circle me-2"></i>
                        {t('duplicateCardManager.messages.importantNote')}
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDuplicateModal(false)}
                        disabled={submitting}
                    >
                        {t('duplicateCardManager.cancel')}
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleCreateDuplicate}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {t('duplicateCardManager.creating')}
                            </>
                        ) : (
                            <>
                                <i className="fa fa-check me-2"></i>
                                {t('duplicateCardManager.createAndPay')}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DuplicateCardManager;
